import { c as _c } from "react/compiler-runtime";
import { BannerData, NavigationPromotion, ProductTileData } from '@spa/components/nav/navigation-promotion.component';
import ImageComponent from '@spa/components/image-component/image.component';
import Link from 'next/link';
import { ReactElement } from 'react';
import classNames from 'clsx';
import { filterDesktop } from '@spa/components/nav/utils';
import styles from './navigation-flyout.component.module.scss';
import { tracker } from '@spa/lib/tracking/tracker';
export interface NavigationBrandLink {
  readonly logoUrl: string | null;
  readonly label: string | null;
  readonly path: string | null;
}
export interface NavigationLink {
  readonly label: string | null;
  readonly path: string | null;
}
export interface NavigationBrandsData {
  readonly items: Array<NavigationBrandLink>;
  readonly headline: string | null;
  readonly headlineLink: NavigationLink | null;
}
export interface NavigationItemData {
  readonly id: string | null;
  readonly label: string | null;
  readonly path: string | null;
  readonly hideDesktop: boolean;
  readonly hideMobile: boolean;
  readonly highlighted: boolean;
  readonly children: ReadonlyArray<NavigationItemData> | null;
}
export interface NavigationCategoriesData {
  readonly items: ReadonlyArray<NavigationItemData>;
}
export interface NavigationPromotionsData {
  readonly product: ProductTileData | null;
  readonly banner: BannerData | null;
}
export interface NavigationFlyoutData {
  readonly categories: NavigationCategoriesData | null;
  readonly brands: NavigationBrandsData | null;
  readonly storeBrands: NavigationBrandsData | null;
  readonly promotions: NavigationPromotionsData | null;
}
export type Props = {
  readonly flyout: NavigationFlyoutData;
  readonly isBurgerMenuFlyout?: boolean;
  readonly fullScreenWidth?: boolean;
};
const MAX_CATEGORIES_DEFAULT = 9;
const BRANDS_VARIANT_MAX_CATEGORIES = 2;
const MAX_CHILDREN_3X3 = 3;
const MAX_CHILDREN_3X2 = 6;
const MAX_BRANDS_DEFAULT = 8;
const BRANDS_VARIANT_MAX_BRANDS = 12;
const CATEGORY_VARIANT_THRESHOLD = 6;
const hasProductOrBanner = (promotion: NavigationPromotionsData): boolean => !!promotion.product || !!promotion.banner;
export const NavigationFlyout = t0 => {
  const $ = _c(37);
  const {
    flyout: t1,
    isBurgerMenuFlyout: t2,
    fullScreenWidth
  } = t0;
  const {
    categories,
    brands,
    storeBrands,
    promotions
  } = t1;
  const isBurgerMenuFlyout = t2 === undefined ? false : t2;
  const isBrandsOnlyVariant = Boolean((brands || storeBrands) && !promotions);
  const isCategoriesAndPromotianAndNoBrandsVariant = Boolean(categories && promotions?.product && !brands && !storeBrands);
  const isFullScreenWidthCategoriesOnlyVariant = Boolean(fullScreenWidth && !brands?.items.length && !storeBrands?.items.length && !promotions?.banner && !promotions?.product);
  const maxCategories = isBrandsOnlyVariant ? BRANDS_VARIANT_MAX_CATEGORIES : MAX_CATEGORIES_DEFAULT;
  let getBrandsSection;
  let t3;
  let t4;
  if ($[0] !== categories || $[1] !== isBrandsOnlyVariant || $[2] !== isBurgerMenuFlyout || $[3] !== isCategoriesAndPromotianAndNoBrandsVariant || $[4] !== isFullScreenWidthCategoriesOnlyVariant || $[5] !== maxCategories) {
    const flyoutCategories = categories?.items ? filterDesktop(categories.items) : [];
    const flyoutCategoriesLimited = flyoutCategories.length >= maxCategories ? flyoutCategories.slice(0, maxCategories) : flyoutCategories;
    const maxChildren = flyoutCategoriesLimited.length <= CATEGORY_VARIANT_THRESHOLD ? MAX_CHILDREN_3X2 : MAX_CHILDREN_3X3;
    let t5;
    if ($[9] !== isBurgerMenuFlyout || $[10] !== maxChildren) {
      t5 = children => {
        const filtered = filterDesktop(children);
        if (!filtered || filtered.length === 0) {
          return <></>;
        }
        const sliced = filtered.slice(0, maxChildren);
        return <ul className={styles["nav-flyout__subcategory-list"]}>{sliced.map(child => <li key={child.id ?? child.path ?? child.label ?? "unknown-child"} className={styles["nav-flyout__subcategory-list-item"]}><Link prefetch={false} href={child.path ?? "/"} onClick={event => tracker.navigationClick({
              click_name: child.label ?? "unknown",
              click_position: isBurgerMenuFlyout ? "burger_menu" : "header_main_navi",
              click_url: event.currentTarget.href
            })} className={`${styles["nav-flyout__subcategory-link"]}`}>{child.label}</Link></li>)}</ul>;
      };
      $[9] = isBurgerMenuFlyout;
      $[10] = maxChildren;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    const getCategoryChildrenList = t5;
    const getCategoryLinkClasses = _temp;
    let t6;
    if ($[12] !== isBrandsOnlyVariant) {
      t6 = brand => <>{brand.logoUrl && <div className={classNames(styles["nav-flyout__brand-image"], {
          [styles["nav-flyout__brand-image--3col"]]: isBrandsOnlyVariant
        })}><ImageComponent src={brand.logoUrl} alt={brand.label ?? ""} width={93} height={74} /></div>}</>;
      $[12] = isBrandsOnlyVariant;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    const brandImage = t6;
    const maxBrands = isBrandsOnlyVariant ? BRANDS_VARIANT_MAX_BRANDS : MAX_BRANDS_DEFAULT;
    let t7;
    if ($[14] !== brandImage || $[15] !== isBrandsOnlyVariant || $[16] !== maxBrands) {
      t7 = t8 => {
        const {
          items,
          headline,
          headlineLink
        } = t8;
        return items && <div className={classNames(styles["nav-flyout__section"], {
          [styles["nav-flyout__section--fixed"]]: !isBrandsOnlyVariant,
          [styles["nav-flyout__section--3col"]]: isBrandsOnlyVariant
        })}><div className={classNames(styles["nav-flyout__brands"], {
            [styles["nav-flyout__brands--3col"]]: isBrandsOnlyVariant
          })}>{headline && <span className="bold">{headlineLink?.path ? <Link prefetch={false} href={headlineLink.path} className={styles["nav-flyout__headline-link"]}>{headline}</Link> : headline}</span>}<ul className={classNames(styles["nav-flyout__brand-list"], {
              [styles["nav-flyout__brand-list--3col"]]: isBrandsOnlyVariant
            })}>{items.slice(0, maxBrands).map(brand_0 => <li key={brand_0.path ?? brand_0.label ?? brand_0.logoUrl ?? "unknown-brand"} className={classNames(styles["nav-flyout__brand-item"], {
                [styles["nav-flyout__brand-item--3col"]]: isBrandsOnlyVariant
              })}>{brand_0.path ? <Link prefetch={false} href={brand_0.path} className={styles["nav-flyout__brand-link"]} aria-label={brand_0.label ?? ""}>{brandImage(brand_0)}</Link> : brandImage(brand_0)}</li>)}</ul></div></div>;
      };
      $[14] = brandImage;
      $[15] = isBrandsOnlyVariant;
      $[16] = maxBrands;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    getBrandsSection = t7;
    if ($[18] !== isCategoriesAndPromotianAndNoBrandsVariant || $[19] !== isFullScreenWidthCategoriesOnlyVariant) {
      t3 = classNames(styles["nav-flyout"], {
        [styles["nav-flyout--with-center-space"]]: isCategoriesAndPromotianAndNoBrandsVariant,
        [styles["nav-flyout--left-aligned"]]: isFullScreenWidthCategoriesOnlyVariant
      });
      $[18] = isCategoriesAndPromotianAndNoBrandsVariant;
      $[19] = isFullScreenWidthCategoriesOnlyVariant;
      $[20] = t3;
    } else {
      t3 = $[20];
    }
    t4 = flyoutCategoriesLimited.length > 0 && <div className={classNames(styles["nav-flyout__section"], {
      [styles["nav-flyout__section--min-content"]]: isBrandsOnlyVariant,
      [styles["nav-flyout__section--half-content"]]: isFullScreenWidthCategoriesOnlyVariant
    })}><ul className={classNames(styles["nav-flyout__category-list"], {
        [styles["nav-flyout__category-list--column"]]: isBrandsOnlyVariant
      })}>{flyoutCategoriesLimited.filter(_temp2).map(category_0 => <li key={category_0.id ?? category_0.path ?? category_0.label ?? "unknown-category"} className={classNames(styles["nav-flyout__category-list-item"], {
          [styles["nav-flyout__category-list-item--fullwidth"]]: isBrandsOnlyVariant
        })}><Link prefetch={false} href={category_0.path ?? "/"} onClick={event_0 => tracker.navigationClick({
            click_name: category_0.label ?? "unknown",
            click_position: isBurgerMenuFlyout ? "burger_menu" : "header_main_navi",
            click_url: event_0.currentTarget.href
          })}><span className={getCategoryLinkClasses(category_0.highlighted ?? false)}>{category_0.label}</span></Link>{category_0.children && getCategoryChildrenList(category_0.children)}</li>)}</ul></div>;
    $[0] = categories;
    $[1] = isBrandsOnlyVariant;
    $[2] = isBurgerMenuFlyout;
    $[3] = isCategoriesAndPromotianAndNoBrandsVariant;
    $[4] = isFullScreenWidthCategoriesOnlyVariant;
    $[5] = maxCategories;
    $[6] = getBrandsSection;
    $[7] = t3;
    $[8] = t4;
  } else {
    getBrandsSection = $[6];
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[21] !== brands || $[22] !== getBrandsSection) {
    t5 = brands && getBrandsSection(brands);
    $[21] = brands;
    $[22] = getBrandsSection;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== getBrandsSection || $[25] !== isBrandsOnlyVariant || $[26] !== storeBrands) {
    t6 = isBrandsOnlyVariant && storeBrands && getBrandsSection(storeBrands);
    $[24] = getBrandsSection;
    $[25] = isBrandsOnlyVariant;
    $[26] = storeBrands;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== brands || $[29] !== promotions) {
    t7 = promotions && hasProductOrBanner(promotions) && <div className={classNames(styles["nav-flyout__section--max-content"], styles["nav-flyout__section--fixed"])}><NavigationPromotion productTileData={promotions.product} banner={promotions.banner} expandBanner={brands === null} bottomAlignBanner={brands !== null} /></div>;
    $[28] = brands;
    $[29] = promotions;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== t3 || $[32] !== t4 || $[33] !== t5 || $[34] !== t6 || $[35] !== t7) {
    t8 = <div className={t3}>{t4}{t5}{t6}{t7}</div>;
    $[31] = t3;
    $[32] = t4;
    $[33] = t5;
    $[34] = t6;
    $[35] = t7;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  return t8;
};
function _temp(highlighted) {
  return classNames("bold", styles["nav-flyout__category-link"], {
    [styles["nav-flyout__category-link--highlighted"]]: highlighted
  });
}
function _temp2(category) {
  return !!category.path;
}